import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { createApplicant, getKYCLink } from 'store/fetchers/users'

import { useTranslation } from 'utils/use-translation'

import { formatNumber } from 'utils/formatters'

import { goToChat } from 'utils/go-to-chat'

import { KycStatuses } from 'store/fetchers/users/types'

import useViewport from 'libs/use-viewport'

import ButtonBlue from '../../../../components/ui/button/_color/_blue'

import BorderedButton from '../../../../components/global/bordered-button'

import Skeleton from '../../../../components/ui/skeleton'

import DiscountInfoMobile from './components/discount-info-mobile'

import styles from './styles.module.scss'
import CoinIcon from './icons/coin.svg'
import PercentIcon from './icons/percent.svg'
import ManagerIcon from './icons/manager.svg'
import ArrowsIcon from './icons/arrows.svg'
import DynamicAprIcon from './icons/dynamic-apr-icon.png'
import { ILoyaltyProgramProps, DiscountLevel } from './loyalty-program.props'
import VerificationIcon from './icons/Verification Icon.svg'
import DiscountInfo from './components/discount-info'
import { discountData } from './data'

export default function LoyaltyProgram({ totalSum, isLoading, isShort, user }: ILoyaltyProgramProps) {
  const { firstCheckpoint, secondCheckpoint } = discountData
  const { t, replacePlaceholdersWithElements } = useTranslation()
  const [view] = useViewport()
  const isMobile = ['xs-mobile', 'mobile'].includes(view)
  const discountLevel = getDiscountLevel(totalSum)
  const [percent, setPercent] = useState<number>(0)
  const router = useRouter()

  useEffect(() => {
    if (totalSum !== 0) {
      setPercent((totalSum * 100) / 5000000)
    }
  }, [totalSum])

  const getNewLoan = () => {
    router.push('/account/loans/add')
  }

  const openSupport = () => {
    goToChat()
  }

  const verifyKYC = async () => {
    try {
      await createApplicant()
      const kycLink = await getKYCLink()

      if (kycLink) {
        window.open(kycLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error(error)
    }
  }

  function getDiscountLevel(totalSum: number) {
    if (totalSum < firstCheckpoint.sum) return DiscountLevel.FIRST
    if (totalSum >= firstCheckpoint.sum && totalSum < secondCheckpoint.sum) return DiscountLevel.SECOND

    return DiscountLevel.THIRD
  }

  return isLoading ? (
    <Skeleton className={styles.loader} />
  ) : (
    <div className={styles.loyalty}>
      <div className={styles.amount}>
        <p>${formatNumber(totalSum)}</p>
        <img src={CoinIcon} alt="coin icon" />
      </div>

      <div className={styles.percent}>
        <div style={{ width: `${percent < 100 ? percent : 100}%` }} className={styles.fill}></div>
        <DiscountInfo isKycVerified={user?.kyc_verified === KycStatuses.VERIFIED} discountLevel={discountLevel} />
      </div>

      <div className={styles.data}>
        <p>
          {t('open_loans_lower_case')}
          <span>/$5 000 000</span>
        </p>
        <p>{t('prime_access')}</p>
      </div>
      <div className={styles.discount}>
        <h4 className={styles.discount__title}>{t('progressive_discount')}</h4>
        <p className={styles.discount__subtitle}>
          {user?.kyc_verified === KycStatuses.VERIFIED ? t('maximize_benefits') : t('increase_discount_mobile')}
        </p>
        <DiscountInfoMobile isKycVerified={user?.kyc_verified === KycStatuses.VERIFIED} discountLevel={discountLevel} />
      </div>

      {!isShort && (
        <>
          <div className={styles.experience}>
            <h4>{t('elevate_crypto_experience')}</h4>

            <div className={styles.experience__data}>
              <div className={styles.item}>
                <img src={PercentIcon} alt="percent icon" />
                <p>{t('customized_rate')}</p>
              </div>

              <div className={styles.item}>
                <img src={DynamicAprIcon} alt="headphones icon" />
                <p>
                  {replacePlaceholdersWithElements(
                    isMobile ? t('dynamic_apr_contact_us_short') : t('dynamic_apr_contact_us'),
                    {
                      contact_us: (
                        <>
                          <br />
                          <span onClick={openSupport} className={styles.btn__support}>
                            {t('contact_us_capital')}
                          </span>
                        </>
                      ),
                    },
                  )}
                </p>
              </div>

              <div className={styles.item}>
                <img src={ManagerIcon} alt="manager icon" />
                <p>{t('account_manager')}</p>
              </div>

              <div className={styles.item}>
                <img src={ArrowsIcon} alt="arrows icon" />
                <p>{t('extended_limits')}</p>
              </div>
            </div>
          </div>

          <div className={styles.requirements}>
            <h4>{t('requirements')}</h4>
            <div className={styles.requirements__item}>
              {totalSum >= 5000000 ? (
                <img src={VerificationIcon} alt={'verification icon'} />
              ) : (
                <div className={styles.wrapper}>
                  <span></span>
                </div>
              )}
              <p>
                {t('total_open_loans')}
                <br /> $5 000 000
              </p>
            </div>

            <div className={styles.requirements__item}>
              {user?.kyc_verified === KycStatuses.VERIFIED ? (
                <img src={VerificationIcon} alt={'verification icon'} />
              ) : (
                <div className={styles.wrapper}>
                  <span></span>
                </div>
              )}
              <p>{t('kyc_verification')}</p>
            </div>
          </div>

          <div className={styles.buttons}>
            <ButtonBlue onClick={getNewLoan} className={styles.btnBlue}>
              {t('get_new_loan')}
            </ButtonBlue>
            {user?.kyc_verified !== KycStatuses.VERIFIED && (
              <BorderedButton onClick={verifyKYC} className={styles.btnBordered}>
                {t('kyc_verification')}
              </BorderedButton>
            )}
          </div>
        </>
      )}
    </div>
  )
}
